import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import { usersAPI } from "../services/api";
import ConfirmDialog from "./ConfirmDialog";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    email: "",
    fullName: "",
    position: "",
    department: "",
    role: "USER",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const { hasPermission, user } = useAuth();

  React.useEffect(() => {
    fetchUsers();
  }, []);

  // Check if user has permission to access this page
  if (!hasPermission("canManageUsers") || user?.role !== "ผู้ดูแลระบบ") {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
          <h2>ไม่มีสิทธิ์เข้าถึง</h2>
          <p>เฉพาะผู้ดูแลระบบเท่านั้นที่สามารถเข้าถึงหน้านี้ได้</p>
          <small style={{ color: "#64748b" }}>
            บทบาทปัจจุบันของคุณ: {user?.role || "ไม่ระบุ"}
          </small>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);

      // Try to fetch from API first
      try {
        const response = await usersAPI.getAll();
        if (response.data.success && response.data.data) {
          setUsers(response.data.data);
          return;
        }
      } catch (apiError) {
        console.warn("API fetch failed, using mock data:", apiError.message);
      }

      // Fallback to mock data
      const mockUsers = [
        {
          id: 1,
          username: "admin",
          email: "admin@gelato.com",
          full_name: "ผู้ดูแลระบบ",
          position: "System Administrator",
          department: "IT",
          role_name: "ผู้ดูแลระบบ",
          is_active: true,
          created_at: "2024-01-15T10:00:00Z",
        },
        {
          id: 2,
          username: "manager",
          email: "manager@gelato.com",
          full_name: "ผู้จัดการระบบ",
          position: "Financial Manager",
          department: "Finance",
          role_name: "ผู้จัดการ",
          is_active: true,
          created_at: "2024-02-01T14:30:00Z",
        },
        {
          id: 3,
          username: "user",
          email: "user@gelato.com",
          full_name: "ผู้ใช้งานทั่วไป",
          position: "Data Entry Clerk",
          department: "Operations",
          role_name: "ผู้ใช้งาน",
          is_active: true,
          created_at: "2024-01-20T09:15:00Z",
        },
      ];

      setUsers(mockUsers);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.fullName) {
      alert("กรุณากรอกข้อมูลที่จำเป็น");
      return;
    }

    try {
      // Try API call first
      try {
        const userData = {
          username: newUser.username,
          password: newUser.password,
          fullName: newUser.fullName,
          userRole: newUser.role,
          workPlace: newUser.department,
          email: newUser.email,
          position: newUser.position,
        };

        const response = await usersAPI.create(userData);
        if (response.data.success) {
          await fetchUsers(); // Refresh the list
          setNewUser({
            username: "",
            password: "",
            email: "",
            fullName: "",
            position: "",
            department: "",
            role: "ผู้ใช้งาน",
          });
          setShowCreateModal(false);
          alert("เพิ่มผู้ใช้สำเร็จ");
          return;
        }
      } catch (apiError) {
        console.warn("API create failed, using mock:", apiError.message);
      }

      // Fallback to mock creation
      const user = {
        id: users.length + 1,
        username: newUser.username,
        email: newUser.email,
        full_name: newUser.fullName,
        position: newUser.position,
        department: newUser.department,
        role_name: newUser.role,
        is_active: true,
        created_at: new Date().toISOString(),
      };

      setUsers([user, ...users]);
      setNewUser({
        username: "",
        password: "",
        email: "",
        fullName: "",
        position: "",
        department: "",
        role: "ผู้ใช้งาน",
      });
      setShowCreateModal(false);
      alert("เพิ่มผู้ใช้สำเร็จ (โหมดทดสอบ)");
    } catch (error) {
      console.error("Failed to create user:", error);
      alert("เกิดข้อผิดพลาดในการเพิ่มผู้ใช้");
    }
  };

  const updateUser = async () => {
    try {
      // Try API call first
      try {
        const response = await usersAPI.update(selectedUser.id, selectedUser);
        if (response.data.success) {
          await fetchUsers(); // Refresh the list
          setShowEditModal(false);
          setSelectedUser(null);
          alert("แก้ไขผู้ใช้สำเร็จ");
          return;
        }
      } catch (apiError) {
        console.warn("API update failed, using mock:", apiError.message);
      }

      // Fallback to mock update
      setUsers(
        users.map((user) =>
          user.id === selectedUser.id ? { ...selectedUser } : user
        )
      );

      setShowEditModal(false);
      setSelectedUser(null);
      alert("แก้ไขผู้ใช้สำเร็จ (โหมดทดสอบ)");
    } catch (error) {
      console.error("Failed to update user:", error);
      alert("เกิดข้อผิดพลาดในการแก้ไขผู้ใช้");
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      // Try API call first
      try {
        const response = await usersAPI.toggleStatus(userId);
        if (response.data.success) {
          await fetchUsers(); // Refresh the list
          return;
        }
      } catch (apiError) {
        console.warn("API toggle failed, using mock:", apiError.message);
      }

      // Fallback to mock toggle
      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, is_active: !user.is_active } : user
        )
      );
    } catch (error) {
      console.error("Failed to toggle user status:", error);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนสถานะผู้ใช้");
    }
  };

  const deleteUser = async (userId) => {
    try {
      // Try API call first
      try {
        const response = await usersAPI.delete(userId);
        if (response.data.success) {
          setUsers(users.filter((user) => user.id !== userId));
          setShowDeleteConfirm(false);
          setUserToDelete(null);
          alert("ลบผู้ใช้สำเร็จ");
          return;
        }
      } catch (apiError) {
        console.warn("API delete failed, using mock:", apiError.message);
      }

      // Fallback to mock deletion
      setUsers(users.filter((user) => user.id !== userId));
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      alert("ลบผู้ใช้สำเร็จ (โหมดทดสอบ)");
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("เกิดข้อผิดพลาดในการลบผู้ใช้");
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "all" || user.role_name === roleFilter;

    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    switch (role) {
      case "ผู้ดูแลระบบ":
        return <span className="badge badge-error">ผู้ดูแลระบบ</span>;
      case "ผู้จัดการ":
        return <span className="badge badge-warning">ผู้จัดการ</span>;
      case "ผู้ใช้งาน":
        return <span className="badge badge-info">ผู้ใช้งาน</span>;
      default:
        return <span className="badge">{role}</span>;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>กำลังโหลดข้อมูลผู้ใช้...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>จัดการผู้ใช้งาน</h1>
          <p>จัดการบัญชีผู้ใช้และสิทธิ์การเข้าถึง (เฉพาะผู้ดูแลระบบ)</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + เพิ่มผู้ใช้ใหม่
        </button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <input
              type="text"
              className="form-input"
              placeholder="ค้นหาชื่อ, ชื่อผู้ใช้, หรืออีเมล..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <select
              className="form-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">บทบาททั้งหมด</option>
              <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>
              <option value="ผู้จัดการ">ผู้จัดการ</option>
              <option value="ผู้ใช้งาน">ผู้ใช้งาน</option>
            </select>
          </div>
          <button className="btn btn-outline" onClick={fetchUsers}>
            🔄 รีเฟรช
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>ชื่อผู้ใช้</th>
                <th>อีเมล</th>
                <th>ตำแหน่ง</th>
                <th>แผนก</th>
                <th>บทบาท</th>
                <th>สถานะ</th>
                <th>วันที่สร้าง</th>
                <th>การดำเนินการ</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: "600" }}>{user.full_name}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        @{user.username}
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.position}</td>
                  <td>{user.department}</td>
                  <td>{getRoleBadge(user.role_name)}</td>
                  <td>
                    <span
                      className={`badge ${
                        user.is_active ? "badge-success" : ""
                      }`}
                    >
                      {user.is_active ? "ใช้งาน" : "ระงับ"}
                    </span>
                  </td>
                  <td>
                    {new Date(user.created_at).toLocaleDateString("th-TH")}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {hasPermission("canManageUsers") && (
                        <>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            title="แก้ไข"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => toggleUserStatus(user.id)}
                            title={user.is_active ? "ระงับ" : "เปิดใช้งาน"}
                          >
                            {user.is_active ? "🔒" : "🔓"}
                          </button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => handleDeleteClick(user)}
                            title="ลบ"
                            style={{ color: "#ef4444" }}
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">เพิ่มผู้ใช้ใหม่</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2" style={{ gap: "16px" }}>
                <div className="form-group">
                  <label className="form-label">ชื่อผู้ใช้ *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newUser.username}
                    onChange={(e) =>
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                    placeholder="ชื่อผู้ใช้"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">รหัสผ่าน *</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    placeholder="รหัสผ่าน"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">อีเมล *</label>
                <input
                  type="email"
                  className="form-input"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  placeholder="อีเมล"
                />
              </div>
              <div className="form-group">
                <label className="form-label">ชื่อ-นามสกุล *</label>
                <input
                  type="text"
                  className="form-input"
                  value={newUser.fullName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, fullName: e.target.value })
                  }
                  placeholder="ชื่อ-นามสกุล"
                />
              </div>
              <div className="grid grid-cols-2" style={{ gap: "16px" }}>
                <div className="form-group">
                  <label className="form-label">ตำแหน่ง</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newUser.position}
                    onChange={(e) =>
                      setNewUser({ ...newUser, position: e.target.value })
                    }
                    placeholder="ตำแหน่ง"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">แผนก</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newUser.department}
                    onChange={(e) =>
                      setNewUser({ ...newUser, department: e.target.value })
                    }
                    placeholder="แผนก"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">บทบาท</label>
                <select
                  className="form-select"
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value })
                  }
                >
                  <option value="ผู้ใช้งาน">ผู้ใช้งาน</option>
                  <option value="ผู้จัดการ">ผู้จัดการ</option>
                  <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowCreateModal(false)}
              >
                ยกเลิก
              </button>
              <button className="btn btn-primary" onClick={createUser}>
                เพิ่มผู้ใช้
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={() => deleteUser(userToDelete?.id)}
        title="ลบผู้ใช้"
        message={`คุณแน่ใจหรือไม่ที่จะลบผู้ใช้ "${userToDelete?.full_name}"? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        type="danger"
      />
    </div>
  );
};

export default Users;
